import React, { useState, useMemo, useEffect } from "react";


const getWeekDays = (offset = 0) => {
  const today = new Date();
  const start = new Date(today);
  start.setDate(today.getDate() - today.getDay() + offset * 7);
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    return d;
  });
};

const IcoChevron = ({ dir = 'down' }) => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    style={{ transform: dir === 'up' ? 'rotate(180deg)' : 'none', transition: 'transform .25s' }}>
    <polyline points="6 9 12 15 18 9" />
  </svg>
);


const WeekCalendar = ({
  value = null,
  selectedDate = null,
  onChange,
  onSelect,
  minDate,
  maxDate,
  isDateDisabled,
  initialMonth,
  className = "",
}) => {
  const formatISODateLocal = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };

  const parseLocalISO = (iso) => {
    if (!iso) return null;
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
    if (!match) {
      const fallback = new Date(iso);
      return isNaN(fallback.getTime()) ? null : fallback;
    }

    const [, y, m, d] = match;
    return new Date(Number(y), Number(m) - 1, Number(d));
  };

  const stripTime = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

  const normalizedSelectedDate = useMemo(() => {
    const incoming = value ?? selectedDate;
    if (!incoming) return null;
    if (incoming instanceof Date) return stripTime(incoming);
    if (typeof incoming === "string") return parseLocalISO(incoming);
    return null;
  }, [value, selectedDate]);

  const [monthView, setMonthView] = useState(() => {
    const base = initialMonth || normalizedSelectedDate || new Date();
    return new Date(base.getFullYear(), base.getMonth(), 1);
  });

  useEffect(() => {
    if (normalizedSelectedDate) {
      setMonthView(
        new Date(
          normalizedSelectedDate.getFullYear(),
          normalizedSelectedDate.getMonth(),
          1
        )
      );
    }
  }, [normalizedSelectedDate]);

  const months = [
    "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
    "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"
  ];

  const weekdays = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];
  const [weekOffset, setWeekOffset] = useState(0);

  const isBeforeMin = (d) => (minDate ? stripTime(d) < stripTime(minDate) : false);
  const isAfterMax = (d) => (maxDate ? stripTime(d) > stripTime(maxDate) : false);

  const isSameDay = (a, b) =>
    !!a &&
    !!b &&
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();

  const today = stripTime(new Date());

  const visibleDays = useMemo(() => {
    const base =
      normalizedSelectedDate && !isBeforeMin(normalizedSelectedDate) && !isAfterMax(normalizedSelectedDate)
        ? normalizedSelectedDate
        : new Date();

    const start = new Date(base);
    start.setHours(0, 0, 0, 0);
    start.setDate(base.getDate() - base.getDay() + weekOffset * 7);

    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      return d;
    });
  }, [normalizedSelectedDate, weekOffset, minDate, maxDate]);


  const firstVisibleDay = visibleDays[0];
  const lastVisibleDay = visibleDays[6];

  const monthLabel =
    firstVisibleDay.getMonth() === lastVisibleDay.getMonth() &&
      firstVisibleDay.getFullYear() === lastVisibleDay.getFullYear()
      ? `${months[firstVisibleDay.getMonth()]} ${firstVisibleDay.getFullYear()}`
      : `${months[firstVisibleDay.getMonth()]} ${firstVisibleDay.getFullYear()} – ${months[lastVisibleDay.getMonth()]} ${lastVisibleDay.getFullYear()}`;

  const canPrevMonth = useMemo(() => {
    if (!minDate) return true;
    const prev = new Date(monthView.getFullYear(), monthView.getMonth() - 1, 1);
    const lastPrev = new Date(prev.getFullYear(), prev.getMonth() + 1, 0);
    return stripTime(lastPrev) >= stripTime(minDate);
  }, [monthView, minDate]);

  const canNextMonth = useMemo(() => {
    if (!maxDate) return true;
    const next = new Date(monthView.getFullYear(), monthView.getMonth() + 1, 1);
    return stripTime(next) <= stripTime(maxDate);
  }, [monthView, maxDate]);

  const changeMonth = (delta) => {
    if (delta < 0 && !canPrevMonth) return;
    if (delta > 0 && !canNextMonth) return;

    const newMonth = new Date(monthView.getFullYear(), monthView.getMonth() + delta, 1);
    setMonthView(newMonth);
  };

  const emitChange = (date) => {
    const iso = date ? formatISODateLocal(date) : null;

    if (onChange) onChange(iso);
    if (onSelect) onSelect(iso);
  };
  const handleSelect = (date) => {
    const outOfRange = isBeforeMin(date) || isAfterMax(date);
    const customDisabled = isDateDisabled ? isDateDisabled(date) : false;

    if (outOfRange || customDisabled) return;

    emitChange(date);
  };

  const canPrevWeek = useMemo(() => {
    if (!minDate) return true;
    return visibleDays.some(d => stripTime(d) > stripTime(minDate)) || stripTime(visibleDays[0]) > stripTime(minDate);
  }, [visibleDays, minDate]);

  const canNextWeek = useMemo(() => {
    if (!maxDate) return true;
    return visibleDays.some(d => stripTime(d) < stripTime(maxDate)) || stripTime(visibleDays[6]) < stripTime(maxDate);
  }, [visibleDays, maxDate]);

  const formatLong = (d) =>
    d?.toLocaleDateString("es-ES", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    }) || "Ninguna fecha seleccionada";



  return (
    <div>
      <div className="ck-cal-header">
        <div className="ck-cal-title">
          {monthLabel}
        </div>
        <div className="ck-cal-nav">
          <button className="ck-cal-nav-btn"
            onClick={() => { setWeekOffset(w => w - 1); }}
            aria-label="Mes anterior">
            <IcoChevron dir="up" />
          </button>
          <button className="ck-cal-nav-btn"
            onClick={() => { setWeekOffset(w => w + 1); }}
            aria-label="Mes siguiente">
            <IcoChevron dir="down" />
          </button>
        </div>
      </div>
      <div className="ck-cal-week">
        {visibleDays.map((date, idx) => {
          const isToday = isSameDay(stripTime(date), today);
          const isSelected =
            normalizedSelectedDate &&
            isSameDay(stripTime(date), stripTime(normalizedSelectedDate));
          const outOfRange = isBeforeMin(date) || isAfterMax(date);
          const customDisabled = isDateDisabled ? isDateDisabled(date) : false;
          const disabled = outOfRange || customDisabled;

          let cls = "ck-cal-day";
          if (isSelected) cls += " selected";
          else if (isToday) cls += " today";
          else cls += " normal";
          if (disabled) cls += " disabled";

          return (
            <div key={idx} className={cls}
              onClick={() => !disabled && handleSelect(date)}
              aria-disabled={disabled}
              disabled={disabled}
            >
              <span className="ck-cal-day-name">{weekdays[date.getDay()]}</span>
              <span className="ck-cal-day-num">{date.getDate()}</span>
            </div>
          );
        })}
      </div>
      <div className="ck-cal-actions">
        <button
          type="button"
          className="ck-cal-action-btn"
          onClick={() => {
            const now = new Date();
            const todayDate = new Date(
              now.getFullYear(),
              now.getMonth(),
              now.getDate()
            );

            const baseToday = new Date();
            baseToday.setHours(0, 0, 0, 0);

            const startOfCurrentWeek = new Date(baseToday);
            startOfCurrentWeek.setDate(baseToday.getDate() - baseToday.getDay());

            const startOfTodayWeek = new Date(todayDate);
            startOfTodayWeek.setDate(todayDate.getDate() - todayDate.getDay());

            const diffWeeks = Math.round(
              (startOfTodayWeek.getTime() - startOfCurrentWeek.getTime()) / (7 * 24 * 60 * 60 * 1000)
            );

            setWeekOffset(diffWeeks);

            const outOfRange = isBeforeMin(todayDate) || isAfterMax(todayDate);
            const customDisabled = isDateDisabled ? isDateDisabled(todayDate) : false;

            if (!outOfRange && !customDisabled) {
              emitChange(todayDate);
            } else {
              emitChange(null);
            }
          }}
        >
          📍 Ir a Hoy
        </button>

        <button
          type="button"
          className="ck-cal-action-btn"
          onClick={() => emitChange(null)}
        >
          🗑️ Limpiar
        </button>
      </div>
    </div >
  );
};

export default WeekCalendar;